// src/configs/applications/appmulti.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  AuditOutlined,
  DeploymentUnitOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  FormOutlined,
  HomeOutlined,
  SearchOutlined,
  SettingOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { TfiWorld } from 'react-icons/tfi';
import { MdOutlineAssignment } from 'react-icons/md';
import { RiListSettingsLine, RiShieldUserLine, RiDiscountPercentLine } from 'react-icons/ri';
import { BsBoxSeam, BsBuilding, BsCashCoin } from 'react-icons/bs';
import { GrGroup } from 'react-icons/gr';
import { FiShoppingBag } from 'react-icons/fi';

// *******************************************************************************
// APP GLOBAL CONFIGURATION
// *******************************************************************************
const APP_MULTI = {
  ID: 'appmulti',
  NAME: 'Multi Seguro Viagem',
  THEME_COLOR: 'CUSTOM_MULTI_APP',
  THEME: 'light',
  FINTECH_SERVICES: true,
  DOMAINS: [
    'sistema.multiseguroviagem.com.br',
    'appmulti-v2.web.app',
    'homologacao.multiseguroviagem.com.br',
    'appmulti-v2-homologation.web.app',
  ],
  PRESET: {
    MULTI_SUBSCRIPTIONS: false,
    PROFILE: 'services',
    NATIVE_RESOURCES: ['moduleresources_quotations'],
    SUGGESTED_RESOURCES: [],
    CAN_CHANGE_RESOURCES: true,
    SHOW_RESOURCES_FIRST_CONFIGURATION: true,
    SHOW_RESOURCES_SUBSCRIPTION: true,
    CONTRACT: {
      BILLING_CYCLE: 'MONTHLY',
      MONTHLY_AMOUNT: 2000,
      DUE_DAY: 20,
      PAYMENT_METHOD: 'BANK_SLIP',
      CURRENCY: 'BRL',
    },
  },
  COPY_RIGHT: 'Multi Seguro Viagem',
  API_PORT_HOMOLOGATION: 9112,
  API_PORT_PRODUCTION: 9012,
  API_HOST_LOCALHOST: 'localhost',
  API_HOST_PRODUCTION: 'vps.jarvisly.com',
  API_INTEGRATED: false,
  TOKEN: import.meta.env.VITE_APPMULTI_TOKEN,
  TOKEN_EXP_MINUTES: 60,

  LOCALE: 'pt-BR',
  // LOCALE: 'en-US',
  LANGUAGE: 'pt-BR',
  // LANGUAGE: 'en-US',
  LANGUAGE_OPTIONS: ['pt-BR', 'pt-PT', 'en-US', 'es-ES'],

  LOGO_APP: '/apps/appmulti/logo-app.png',
  LOGO_APP_WHITE: '/apps/appmulti/logo-app-white.png',

  LOGO_APP_ICON: '/apps/appmulti/logo-app-icon.png',
  LOGO_APP_ICON_WHITE: '/apps/appmulti/logo-app-icon-white.png',

  LOGO: '/apps/appmulti/logo.png',
  LOGO_WHITE: '/apps/appmulti/logo-white.png',
  LOGO_LOGIN_SIDE: '/apps/appmulti/logo-app-side.png',

  PWA_NAME: { production: 'Multi Seguro Viagem', homologation: 'Multi SV Test' },
  PWA_SHORT_NAME: { production: 'Multi SV', homologation: 'Multi Test' },
  PWA_DESCRIPTION: 'Plataforma de seguro viagem',
  TITLE: 'Multi Seguro Viagem',
  '//SLOGAN': 'condominios_inteligentes',
  SLOGAN: '',
  SLOGAN2: '',
  SLOGAN_REGISTRATION: 'registration_slogan',
  SLOGAN_REGISTRATION2: '',
  APPLICATION_LAYER: 'adm',
  LOGIN_IDENTIFICATION: 'email',

  // SELF_REGISTRATION can be:
  // - boolean: true/false (same for all environments)
  // - object: { homologation: true/false, production: true/false } (different per environment)
  SELF_REGISTRATION: {
    homologation: false,
    production: false,
  },
  // MOBILE_VALIDATION can be:
  // - false: never asks for mobile
  // - true or []: asks for mobile but doesn't send SMS
  // - ['pt-BR', 'en-US', ...]: asks for mobile and sends SMS for specified countries (BCP 47 format)
  // - object: { homologation: value, production: value } for different behavior per environment
  SELF_REGISTRATION_MOBILE_VALIDATION: ['pt-BR'],
  USER_INVITATION_MOBILE_VALIDATION: ['pt-BR'],

  GOOGLE_LOGIN: false,
  FACEBOOK_LOGIN: false,
  RESET_PASSWORD: true,
  NAVIGATION_TYPE: 'side', // 'side', 'top' or 'tile'

  MODULES: [
    'customers',
    'companies',
    'appmultiOperators',
    'plans',
    'appmultiQuotations',
    'receivables',
    'orders',
    'appmultiLeads',
    'coupons',
  ],
  SHOW_ALL_MENUS: false,
  BREADCRUMB_COLOR: '#FAFAFBFF',

  WELCOME_NEXT_STEPS: [
    'explore_the_system',
    'complete_your_profile',
    'create_your_first_subscription',
    // 'invite_your_team',
  ],

  MENU_APPLICATION: [
    {
      path: '/app/account',
      title: 'account_settings',
      icon: SettingOutlined,
      moduleId: 'account',
    },
  ],

  MENU_NAVIGATION: [
    // ******************
    // DASHBOARD
    // ******************
    {
      path: '/app/dashboard/overview',
      title: 'home',
      icon: HomeOutlined,
      moduleId: 'welcome',
      isGroupTitle: true,
    },

    // ******************
    // QUERIES (CONSULTAS)
    // ******************
    {
      path: '/app/queries',
      title: 'queries',
      icon: SearchOutlined,
      isGroupTitle: true,
      // Group only visible if has lookup resource
      requiredResources: ['moduleresources_lookup'],
      submenu: [
        {
          // path auto-calculated: /app/queries/data-search
          title: 'people_and_companies',
          icon: SearchOutlined,
          moduleId: 'dataSearch',
          // Visible only if has lookup resource (CPF/CNPJ queries)
          requiredResources: ['moduleresources_lookup'],
        },
      ],
    },

    // ******************
    // REGISTRATIONS (CADASTROS)
    // ******************
    {
      path: '/app/registrations',
      title: 'records',
      icon: FormOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'companies',
          icon: BsBuilding,
          moduleId: 'companies',
          // Visible if subscription has multicompany resource
          requiredResources: ['moduleresources_multicompany'],
        },
        {
          title: 'customers',
          icon: GrGroup,
          moduleId: 'customers',
        },
        {
          // path auto-calculated: /app/registrations/appmulti-operators
          // title: 'operators_and_plans',
          title: 'operators',
          icon: TfiWorld,
          moduleId: 'appmultiOperators',
          requiredResources: ['moduleresources_quotations'],
        },
        {
          // path auto-calculated: /app/registrations/plans
          title: 'plans',
          icon: MdOutlineAssignment,
          moduleId: 'plans',
          requiredResources: ['moduleresources_quotations'],
        },
        {
          title: 'products',
          icon: BsBoxSeam,
          moduleId: 'products',
          // Visible if has NFe or NFCe resource
          requiredResources: ['moduleresources_nfe', 'moduleresources_nfce'],
        },
        // // Divider before users
        // {
        //   isDivider: true,
        //   requiredResources: ['moduleresources_multiuser'],
        // },
        {
          title: 'users',
          icon: RiShieldUserLine,
          // icon: LiaUsersCogSolid,
          // icon: TeamOutlined,
          moduleId: 'users',
          // Visible if has multiuser resource
          requiredResources: ['moduleresources_multiuser'],
        },
      ],
    },

    // ******************
    // COMMERCIAL
    // ******************
    {
      title: 'commercial',
      isGroupTitle: true,
      submenu: [
        {
          path: '/app/commercial/travel-insurance-leads',
          title: 'leads',
          icon: DeploymentUnitOutlined,
          moduleId: 'appmultiLeads',
          requiredResources: ['moduleresources_quotations'],
        },
        {
          path: '/app/commercial/travel-insurance-quotations',
          title: 'quotations',
          icon: TagsOutlined,
          moduleId: 'appmultiQuotations',
          requiredResources: ['moduleresources_quotations'],
        },
        {
          path: '/app/commercial/travel-insurance-orders',
          title: 'orders',
          icon: FiShoppingBag,
          moduleId: 'orders',
          requiredResources: ['moduleresources_quotations'],
        },
      ],
    },

    // ******************
    // FISCAL
    // ******************
    {
      path: '/app/fiscal',
      title: 'fiscal',
      icon: FileTextOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'invoices',
          icon: FileProtectOutlined,
          moduleId: 'invoices',
          // Visible if has NFe OR NFCe resource
          requiredResources: ['moduleresources_nfe', 'moduleresources_nfce'],
        },
        {
          path: '/app/fiscal/service-invoices',
          title: 'service_invoices',
          icon: AuditOutlined,
          moduleId: null,
          // Visible if has NFSe resource
          requiredResources: ['moduleresources_nfse'],
        },
        {
          title: 'fiscal_settings',
          // icon: SettingOutlined,
          icon: RiListSettingsLine,
          moduleId: 'fiscalConfig',
          // Visible if has any fiscal resource
          requiredResources: [
            'moduleresources_nfe',
            'moduleresources_nfce',
            'moduleresources_nfse',
          ],
        },
      ],
    },

    // ******************
    // FINANCIAL (FINANCEIRO)
    // ******************
    {
      path: '/app/financial',
      title: 'financial',
      icon: BsCashCoin,
      isGroupTitle: true,
      submenu: [
        {
          path: '/app/financial/receivables',
          title: 'charges',
          icon: BsCashCoin,
          moduleId: 'receivables',
          requiredResources: ['moduleresources_quotations'],
        },
        {
          path: '/app/financial/discount_coupons',
          title: 'discount_coupons',
          icon: RiDiscountPercentLine,
          moduleId: 'coupons',
          requiredResources: ['moduleresources_quotations'],
        },
      ],
    },
  ],
  FIREBASE_CONFIG: JSON.parse(import.meta.env.VITE_APPMULTI_FIREBASE_CONFIG),
  MOCKUP: {
    AUTH: {
      SIGNIN: {
        email: 'me@biruel.dev',
        password: 'idadm123',
      },
      SIGNUP: {
        // step 0
        countryCode: 'BR',
        // countryCode: 'US',
        name: 'Suporte Lekkus',
        email: 'me@biruel.dev',

        // step 1
        emailCode: 1234,
        emailConfirmation: true,

        // step 2
        // phone: {
        //   value: '19982472235',
        //   countryCode: 'BR',
        //
        //   // value: '6509849382',
        //   // countryCode: 'US',
        // },

        // step 3
        mobileCode: 1234,
        mobileConfirmation: true,

        // step 4
        password: 'idadm123',
      },
      RESET_PASSWORD: {
        // step 0
        email: 'me@biruel.dev',

        // step 1
        emailCode: 1234,

        // step 2
        password: 'idadm123',
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default APP_MULTI;
